import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCY } from '../../../../../../config/appConfig';
import { currencyFormatterHelper } from '../../../../../../helpers/currencyFormatterHelper';
import { formatError } from '../../../../../../services/axiosService';
import { FsButton } from '../../../../../../shared/components/button';
import { DialogWrapper } from '../../../../../../shared/components/common/dialogWrapper';
import { InvestorDashboardService } from '../../investorDashboardService';

const DetailRow = ({ label, value }) => (
	<Box className="flex justify-between items-start gap-4 py-2">
		<Box className="!text-sm !text-investor-slate whitespace-nowrap">{label}</Box>
		<Box className="!text-sm !font-bold !text-investor-deep text-end break-all">{value}</Box>
	</Box>
);

export const DisburseDialog = ({ state, loan, onClose = () => {}, onDisbursed = () => {} }) => {
	const { t } = useTranslation();
	const investorDashboardService = new InvestorDashboardService();
	const [preview, setPreview] = useState(null);
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		if (!state || !loan?.loanId) {
			return;
		}

		setPreview(null);
		setError('');
		setLoading(true);
		investorDashboardService
			.getDisbursePreview(loan.loanId)
			.then((resp) => setPreview(resp.data))
			.catch((err) => setError(formatError(err?.response?.data)))
			.finally(() => setLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state, loan?.loanId]);

	const hasIban = !!preview?.iban;
	const canConfirm = !loading && !submitting && !!preview && hasIban;

	const onConfirm = () => {
		setError('');
		setSubmitting(true);
		investorDashboardService
			.disburseLoan(loan.loanId)
			.then(() => onDisbursed())
			.catch((err) => setError(formatError(err?.response?.data)))
			.finally(() => setSubmitting(false));
	};

	return (
		<DialogWrapper
			maxWidth="sm"
			state={state}
			preventOutsideClick
			loading={loading}
			title={t('disburseLoan')}
			onClose={() => onClose(false)}
			content={
				<Box className="flex flex-col p-2">
					{preview ? (
						<>
							<DetailRow label={t('borrowerName')} value={preview.borrowerName} />
							<DetailRow label={t('businessName')} value={preview.businessName} />
							<DetailRow
								label={t('disburseAmount')}
								value={`${CURRENCY} ${currencyFormatterHelper(preview.payoutAmount)}`}
							/>
							<DetailRow label={t('iban')} value={preview.iban || '-'} />
						</>
					) : null}

					{preview && !hasIban ? (
						<Box className="!text-sm !text-red-500 mt-3">{t('borrowerHasNoIban')}</Box>
					) : null}

					{error ? <Box className="!text-sm !text-red-500 mt-3">{error}</Box> : null}
				</Box>
			}
			actions={
				<Box className="flex items-center justify-end gap-3 w-full">
					<FsButton variant="outline" disabled={submitting} onClick={() => onClose(false)}>
						{t('cancel')}
					</FsButton>
					<FsButton loading={submitting} disabled={!canConfirm} onClick={onConfirm}>
						{t('confirm')}
					</FsButton>
				</Box>
			}
		/>
	);
};
